import { Dispatch, FC, SetStateAction } from 'react';
import { motion } from 'motion/react';
import TeacherDiagnosticWidget, { DiagnosticWidgetType } from './TeacherDiagnosticWidget';
import { ProgressBar } from 'primereact/progressbar';
import { Button } from 'primereact/button';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { useUserActionMutation } from '../../../custom-hooks/useUserActionMutation';
import { useAppSelector } from '../../../redux/hooks';
import { selectUser } from '../../../redux/reducers/userSlice';
import { Toast } from 'primereact/toast';
import { EXIT_MS, useFirstMonthGuideCelebration } from '../../../custom-hooks/useFirstMonthGuideCelebration';

interface Props {
  setDismissGuide: Dispatch<SetStateAction<boolean>>;
  widgetCompleted: WidgetCompleted[];
  onWidgetCompleted: (widgetType: DiagnosticWidgetType) => void;
  toastRef: React.RefObject<Toast> | null;
}

export interface WidgetCompleted {
  name: DiagnosticWidgetType;
  checked: boolean;
  blocked: boolean;
}

export const widgetInitialState: WidgetCompleted[] = [
  {
    name: DiagnosticWidgetType.REVIEW_CLASSES,
    checked: false,
    blocked: false,
  },
  {
    name: DiagnosticWidgetType.PREPARE,
    checked: false,
    blocked: false,
  },
  {
    name: DiagnosticWidgetType.UNDERSTAND_STUDENT_EXPERIENCE,
    checked: false,
    blocked: false,
  },
  {
    name: DiagnosticWidgetType.CLASS_FOCUS,
    checked: false,
    blocked: false,
  },
];

const TeacherDiagnosticWidgetWrapper: FC<Props> = ({
  setDismissGuide,
  widgetCompleted,
  onWidgetCompleted,
  toastRef,
}) => {
  const completedSteps = widgetCompleted.filter(el => el.checked).length;
  const progress = (completedSteps / widgetCompleted.length) * 100;
  const [commitMutation] = useUserActionMutation();
  const userRedux = useAppSelector(selectUser);

  const closableDialog = () => {
    confirmDialog({
      header: 'Permanently remove this guide?',
      message: "This will permanently remove your onboarding guide. You won't be able to bring it back.",
    });
  };

  /**
   * Retires the guide for good. Both exits share the same action type — the guide is
   * gone either way — but keep distinct descriptions so analytics can tell a teacher
   * who gave up on it from one who finished every task.
   */
  const retireGuide = () => {
    commitMutation({
      variables: {
        input: {
          userAction: {
            userId: `UserNode:${userRedux?.id}`,
            actionTypeId: 'ActionTypeNode:21',
            timeStamp: new Date().toISOString(),
            description: 'Dismiss First Month Tasks Guide',
          },
        },
      },
    });
    setDismissGuide(true);
  };

  const handleDismissGuide = () => {
    toastRef?.current?.show({
      severity: 'success',
      summary: 'Onboarding guide dismissed',
      detail: 'You can always reach out to support if you need help getting started',
      life: 3000,
    });
    setTimeout(() => {
      toastRef?.current?.clear();
    }, 3000);
    retireGuide();
  };

  // No toast on this path: the celebration itself is the feedback, and a "guide
  // dismissed" message would read as a consolation prize right after the confetti.
  const { hasStarted, isPulsing, isLeaving } = useFirstMonthGuideCelebration({
    allTasksCompleted: widgetCompleted.length > 0 && completedSteps === widgetCompleted.length,
    onFinish: () => retireGuide(),
  });

  return (
    <>
      <ConfirmDialog
        content={({ hide, message }) => (
          <div className="tw-flex tw-flex-col tw-p-5 tw-border-round ">
            <div className="tw-flex tw-flex-col tw-items-left">
              <span className="tw-font-bold tw-text-2xl tw-mb-2 tw-font-atkinson ">{message.header}</span>
              <p className="tw-text-lg tw-font-atkinson">{message.message}</p>
            </div>
            <div className="nc_exit-slip-close-modal">
              <Button label="Keep guide" outlined onClick={e => hide(e)}></Button>
              <Button
                label="Dismiss permanently"
                className="nc_exit-slip-close-button"
                onClick={e => {
                  handleDismissGuide();
                  hide(e);
                }}
              ></Button>
            </div>
          </div>
        )}
        className="!tw-max-w-[570px]"
        closable={false}
      />
      <motion.div
        // The parent only toggles a `tw-hidden` class, so the panel stays mounted and
        // can fade on its own before `setDismissGuide` collapses the column.
        animate={{ opacity: isLeaving ? 0 : 1, scale: isLeaving ? 0.97 : 1 }}
        transition={{ duration: EXIT_MS / 1000, ease: 'easeInOut' }}
        className="tw-flex tw-flex-col tw-gap-y-5 tw-p-5 tw-border-[4px] tw-rounded-2xl tw-border-solid tw-border-indigo-300"
      >
        <div className="tw-flex tw-flex-col tw-gap-y-2  tw-items-start tw-justify-center ">
          <h1 className="tw-text-base tw-font-sora tw-text-neutral-950 tw-font-bold">
            {hasStarted ? "You're all set up!" : 'Prepare for your first month'}
          </h1>
          <div className="tw-flex tw-flex-row tw-items-center tw-w-full tw-gap-x-2">
            <ProgressBar
              pt={{ value: { className: 'tw-bg-lime-500' } }}
              className={`tw-w-[80%] tw-h-[12px] tw-rounded-full${isPulsing ? ' nc_pulse-attention' : ''}`}
              showValue={false}
              value={progress}
              aria-label="progressbar"
              aria-labelledby="progressbar"
            ></ProgressBar>
            <span className="tw-text-xs tw-font-atkinson tw-text-neutral-950 tw-w-[20%]">
              <span className="tw-font-bold">{completedSteps}</span>/{widgetCompleted.length} steps
            </span>
          </div>
        </div>
        {[...widgetCompleted]
          // Actionable tasks first, then blocked, then completed — all in one pass.
          .sort((a, b) => Number(a.checked) - Number(b.checked) || Number(a.blocked) - Number(b.blocked))
          .map(el => {
            return (
              <TeacherDiagnosticWidget
                key={el.name}
                widgetType={el.name}
                checked={el.checked}
                blocked={el.blocked}
                onWidgetCompleted={onWidgetCompleted}
              />
            );
          })}
        <Button
          label="Dismiss this guide"
          className="tw-text-indigo-800 tw-my-2"
          link
          // Once the celebration is running the guide is already on its way out.
          disabled={hasStarted}
          onClick={() => closableDialog()}
        />
      </motion.div>
    </>
  );
};

export default TeacherDiagnosticWidgetWrapper;
